// Close a non-conformance before the flow completed (issue #747).
//
// The normal close writes a final report and assumes the case reached
// monitoring. A case that should never have been raised -- a duplicate, one the
// supplier resolved off-system, or a test -- has no conclusion to report, and
// pushing it through the 8D flow just to close it puts fiction in the record.
//
// Deliberately low-emphasis and behind a confirmation, like the RMA equivalent:
// this is an escape hatch, not a normal step.

import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import fieldConstants from '../constants/rmaFieldConstants.json';

const API_BASE = process.env.REACT_APP_API_URL;
const CLOSURE_REASONS = fieldConstants.prematureClosureReasons || [];

export default function CloseComplaintEarly({ complaint, onClosed }) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(CLOSURE_REASONS[0]?.value || 'other');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isTest = category === 'test_instance';

  const confirm = async () => {
    if (!reason.trim()) {
      setError('A reason is required to close a complaint early.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/complaints/${complaint._id}/close-early`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category, reason: reason.trim(), notes: notes.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error === 'ALREADY_CLOSED'
          ? 'This complaint is already closed.'
          : 'Could not close the complaint. Please try again.');
        setSaving(false);
        return;
      }
      if (onClosed) onClosed(data.complaint);
    } catch (err) {
      setError('Could not close the complaint. Please try again.');
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ni-button ni-button--ghost"
        style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', opacity: 0.75 }}
      >
        Close early…
      </button>
    );
  }

  return (
    <div style={{
      border: `1px solid ${colors.panelBorder}`,
      borderRadius: 10,
      padding: '0.75rem',
      background: 'rgba(255,255,255,0.04)',
      textAlign: 'left',
      maxWidth: 420,
    }}>
      <div style={{ fontWeight: 700, color: colors.textStrong, fontSize: '0.88rem', marginBottom: '0.4rem' }}>
        Close this complaint early
      </div>
      <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: colors.textMuted, lineHeight: 1.5 }}>
        No final report is written and the supplier is not notified — nothing was concluded.
        The reason is kept on the case.
      </p>

      <label style={{ fontSize: '0.75rem', color: colors.textMuted, display: 'block', marginBottom: '0.2rem' }}>
        Category
      </label>
      <select
        value={category}
        onChange={e => setCategory(e.target.value)}
        className="ni-input"
        style={{ width: '100%', marginBottom: '0.5rem', fontSize: '0.82rem' }}
      >
        {CLOSURE_REASONS.map(r => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </select>

      {isTest && (
        <p style={{
          margin: '0 0 0.5rem', fontSize: '0.76rem', lineHeight: 1.5,
          color: '#fbbf24', background: 'rgba(251,191,36,0.08)',
          border: '1px solid rgba(251,191,36,0.28)', borderRadius: 8, padding: '0.4rem 0.55rem',
        }}>
          Marks this as a test case and keeps it out of the retrieval corpus, so it
          cannot be learned from as a real non-conformance. It is not yet excluded
          from the quality figures — that filtering does not exist yet.
        </p>
      )}

      <label style={{ fontSize: '0.75rem', color: colors.textMuted, display: 'block', marginBottom: '0.2rem' }}>
        Reason
      </label>
      <input
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Why is this being closed without a conclusion?"
        className="ni-input"
        style={{ width: '100%', marginBottom: '0.5rem', fontSize: '0.82rem' }}
      />

      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Extra context (optional)"
        rows={2}
        className="ni-input"
        style={{ width: '100%', marginBottom: '0.6rem', fontSize: '0.82rem', resize: 'vertical' }}
      />

      {error && <p style={{ color: colors.danger, fontSize: '0.8rem', margin: '0 0 0.5rem' }}>{error}</p>}

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          type="button"
          onClick={confirm}
          disabled={saving}
          className="ni-button ni-button--danger"
          style={{ fontSize: '0.8rem', opacity: saving ? 0.6 : 1 }}
        >
          {saving ? 'Closing…' : 'Close complaint'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(''); }}
          className="ni-button ni-button--ghost"
          style={{ fontSize: '0.8rem' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
